import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiError } from '@/api/client'

/**
 * The backend's refusal codes, mapped to the catalogue. Written out rather than interpolated into
 * `t(\`users.errors.${code}\`)`: the key strings are typed, so a code without a translation is a
 * compile error here instead of a raw key rendered into the UI.
 *
 * Mirrors `Refusal` and the surrounding exception codes in wp-lead-backend, plus every
 * `MasterDataException` a feature declares.
 */
const REFUSAL_KEYS = {
  SELF_LOCK: 'users.errors.SELF_LOCK',
  SELF_DELETE: 'users.errors.SELF_DELETE',
  SELF_DEMOTE: 'users.errors.SELF_DEMOTE',
  LAST_ADMIN: 'users.errors.LAST_ADMIN',
  ACCOUNT_DELETED: 'users.errors.ACCOUNT_DELETED',
  NOT_INVITED: 'users.errors.NOT_INVITED',
  PASSWORD_POLICY: 'users.errors.PASSWORD_POLICY',
  EMAIL_ALREADY_TAKEN: 'users.errors.EMAIL_ALREADY_TAKEN',
  EMAIL_ALREADY_VERIFIED: 'users.errors.EMAIL_ALREADY_VERIFIED',
  USER_NOT_FOUND: 'users.errors.USER_NOT_FOUND',
  MAIL_NOT_SENT: 'users.errors.MAIL_NOT_SENT',
  TOO_MANY_REQUESTS: 'users.errors.TOO_MANY_REQUESTS',
  HANDOVER_TARGET_UNAVAILABLE: 'users.errors.HANDOVER_TARGET_UNAVAILABLE',
  INVALID_HANDOVER: 'users.errors.INVALID_HANDOVER',

  TRADE_FAIR_NOT_FOUND: 'tradeFairs.errors.TRADE_FAIR_NOT_FOUND',
  TRADE_FAIR_INVALID_PERIOD: 'tradeFairs.errors.TRADE_FAIR_INVALID_PERIOD',
  TRADE_FAIR_DUPLICATE: 'tradeFairs.errors.TRADE_FAIR_DUPLICATE',
  TRADE_FAIR_IN_USE: 'tradeFairs.errors.TRADE_FAIR_IN_USE',

  CUSTOMER_NOT_FOUND: 'customers.errors.CUSTOMER_NOT_FOUND',
  CONTACT_NOT_FOUND: 'customers.errors.CONTACT_NOT_FOUND',
  CUSTOMER_NUMBER_DUPLICATE: 'customers.errors.CUSTOMER_NUMBER_DUPLICATE',
  CUSTOMER_DELETED: 'customers.errors.CUSTOMER_DELETED',
  CUSTOMER_IMPORT_CONFLICT: 'customers.errors.CUSTOMER_IMPORT_CONFLICT',

  LEAD_NOT_FOUND: 'leads.errors.LEAD_NOT_FOUND',
  LEAD_NOT_OWNER: 'leads.errors.LEAD_NOT_OWNER',
  LEAD_TRADE_FAIR_NOT_FOUND: 'leads.errors.LEAD_TRADE_FAIR_NOT_FOUND',
  LEAD_INTEREST_NOT_FOUND: 'leads.errors.LEAD_INTEREST_NOT_FOUND',
  LEAD_CUSTOMER_ALREADY_LINKED: 'leads.errors.LEAD_CUSTOMER_ALREADY_LINKED',
  LEAD_NOTE_NOT_FOUND: 'leads.errors.LEAD_NOTE_NOT_FOUND',
  LEAD_NOTE_NOT_AUTHOR: 'leads.errors.LEAD_NOTE_NOT_AUTHOR',

  FILE_NOT_FOUND: 'businessCards.errors.FILE_NOT_FOUND',
  FILE_UNSUPPORTED_TYPE: 'businessCards.errors.FILE_UNSUPPORTED_TYPE',
  FILE_EMPTY: 'businessCards.errors.FILE_EMPTY',
  FILE_TOO_MANY_PIXELS: 'businessCards.errors.FILE_TOO_MANY_PIXELS',
  FILE_TOO_MANY_CARDS: 'businessCards.errors.FILE_TOO_MANY_CARDS',

  PRODUCT_CATEGORY_NOT_FOUND: 'catalog.errors.PRODUCT_CATEGORY_NOT_FOUND',
  PRODUCT_INTEREST_NOT_FOUND: 'catalog.errors.PRODUCT_INTEREST_NOT_FOUND',
  PRODUCT_CATEGORY_DUPLICATE: 'catalog.errors.PRODUCT_CATEGORY_DUPLICATE',
  PRODUCT_INTEREST_DUPLICATE: 'catalog.errors.PRODUCT_INTEREST_DUPLICATE',

  /** Two people saved the same record; the second one has to reload before trying again. */
  CONCURRENT_MODIFICATION: 'common.errors.CONCURRENT_MODIFICATION',
} as const

export type RefusalCode = keyof typeof REFUSAL_KEYS

export function isRefusal(error: unknown, code: RefusalCode): boolean {
  return error instanceof ApiError && error.code === code
}

/**
 * Turns a rejected request into a sentence worth showing.
 *
 * Three tiers, in order: the translated wording for a known refusal code; failing that the
 * backend's own `detail`, which is English but specific; failing that the caller's generic
 * message. Specific and in the wrong language still beats vague and in the right one.
 *
 * @returns a function taking the error and an already-translated fallback.
 */
export function useApiErrorMessage(): (error: unknown, fallback: string) => string {
  const { t } = useTranslation()

  return useCallback(
    (error: unknown, fallback: string) => {
      if (!(error instanceof ApiError)) {
        return fallback
      }
      if (error.code && error.code in REFUSAL_KEYS) {
        return t(REFUSAL_KEYS[error.code as RefusalCode])
      }
      return error.detail ?? fallback
    },
    [t],
  )
}
